/**
 * @description
 * User Management page component for managing users across all clients.
 * Provides client selection, user listing, and user management functionality.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 */

import React, { useState, useEffect } from 'react';
import { Plus, Users, Search, RefreshCw, Download, Key, Globe } from 'lucide-react';
import { api } from '../services/api';
import { useClientUsers } from '../hooks/useClientUsers';
import { toast } from 'sonner';
import { UserManagementModal } from '../components/ClientUsers/UserManagementModal';
import { RoleAndKeyModal } from '../components/ClientUsers/RoleAndKeyModal';
import { PlatformSyncDialog } from '../components/ClientUsers/PlatformSyncDialog';
import { ClientUser } from '../services/api';

// Types
interface Client {
  id: string;
  subdomain: string;
  name: string;
  status: 'active' | 'inactive' | 'suspended' | 'deploying';
}

// Extended ClientUser type with Anthropic properties
interface ExtendedClientUser extends ClientUser {
  anthropicWorkspaceId?: string;
  anthropicApiKeyPreview?: string;
  anthropicApiKeyAssignedAt?: string;
}

// Main UserManagement component - Updated with null safety checks
const UserManagement: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [adminUsers, setAdminUsers] = useState<Record<string, unknown>[]>([]); // To store users with super admin flag
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showPlatformSyncDialog, setShowPlatformSyncDialog] = useState(false);
  const [showRoleAndKeyModal, setShowRoleAndKeyModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ClientUser | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [platformSyncStatus, setPlatformSyncStatus] = useState<{
    isConfigured: boolean;
    isActive: boolean;
    authenticatedUserEmail?: string;
    lastVerifiedAt?: string;
  } | null>(null);

  const {
    users,
    loading,
    error,
    pagination,
    refetch,
    deleteUser,
    searchUsers,
    filterUsers,
    setPage,
  } = useClientUsers(selectedClientId);

  // const { syncUsers: _syncUsers } = usePlatformSync();

  // Fetch clients and platform sync status on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clients = await api.getClients();
        setClients(clients);
        if (clients.length > 0) {
          setSelectedClientId(clients[0].id);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        toast.error('Failed to fetch clients');
      }
    };

    const loadPlatformSyncStatus = async () => {
      try {
        const status = await api.getPlatformOAuthConfig();
        setPlatformSyncStatus(status.config);
      } catch (error) {
        console.error('Failed to load platform sync status:', error);
        setPlatformSyncStatus({
          isConfigured: false,
          isActive: false,
        });
      }
    };

    const fetchAdminUsers = async () => {
      try {
        // This assumes an API endpoint /api/users exists to fetch all admin users
        const allUsers = await api.getUsers();
        setAdminUsers(allUsers);
      } catch (error) {
        console.error('Error fetching admin users:', error);
        toast.error('Failed to fetch system users');
      }
    };

    fetchClients();
    fetchAdminUsers();
    loadPlatformSyncStatus();
  }, []);

  // Handle search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchUsers]);

  // Handle filters
  useEffect(() => {
    filterUsers({
      role: filterRole || undefined,
      status: filterStatus || undefined,
    });
  }, [filterRole, filterStatus, filterUsers]);

  const handleAddUser = (_user: ClientUser) => {
    toast.success('User added successfully');
  };

  const handleSyncComplete = (result: { added: number; updated: number }) => {
    toast.success(`Sync completed: ${result.added} added, ${result.updated} updated`);
    refetch();
  };

  const handleDeleteUser = async (userId: string) => {
    if (confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId);
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleExportUsers = async () => {
    try {
      toast.loading('Exporting users...');
      
      // Build query parameters
      const params = new URLSearchParams();
      params.set('format', 'csv');
      
      if (searchQuery) {
        params.set('search', searchQuery);
      }
      
      if (filterRole) {
        params.set('role', filterRole);
      }

      // Make request to export endpoint
      const response = await fetch(`/api/users/export?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'text/csv',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export users');
      }

      // Get the CSV content
      const csvContent = await response.text();
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Users exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export users');
    }
  };

  const openRoleAndKeyModal = (user: ClientUser) => {
    setSelectedUser(user);
    setShowRoleAndKeyModal(true);
  };

  return (
    <div className="container mx-auto py-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">User Management</h1>
            {/* Platform Sync Status */}
            <div className="flex items-center gap-2 mt-1">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Platform Sync: 
                {platformSyncStatus?.isActive ? (
                  <span className="text-green-600 font-medium ml-1">
                    Connected ({platformSyncStatus.authenticatedUserEmail})
                  </span>
                ) : (
                  <span className="text-red-600 font-medium ml-1">
                    Not Connected
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPlatformSyncDialog(true)}
            disabled={!selectedClientId}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Sync from Platform</span>
          </button>
          <button
            onClick={handleExportUsers}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-accent hover:text-accent-foreground"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={() => setShowAddUserModal(true)}
            disabled={!selectedClientId}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Client Selection */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
        <h2 className="text-lg font-semibold text-card-foreground mb-4">Select Client</h2>
        {(!clients || clients.length === 0) ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No clients found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(clients || []).map((client) => (
              <button
                key={client.id}
                onClick={() => setSelectedClientId(client.id)}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  selectedClientId === client.id
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-border/80'
                }`}
              >
                <div className="font-medium text-foreground">{client.name}</div>
                <div className="text-sm text-muted-foreground">{client.subdomain}</div>
                <div className={`text-xs mt-1 px-2 py-1 rounded-full inline-block ${
                  client.status === 'active' 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {client.status}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filters and Search */}
      {selectedClientId && (
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search users by email or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
              >
                <option value="">All Roles</option>
                <option value="developer">Developer</option>
                <option value="manager">Manager</option>
                <option value="owner">Owner</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="removed">Removed</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      {selectedClientId && (
        <div className="bg-card rounded-lg shadow-sm border border-border">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-card-foreground">
              Users ({pagination?.total || 0})
            </h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-destructive">
              Error loading users: {error}
            </div>
          ) : !users || users.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              No users found for the selected client.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      API Keys
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Anthropic Assignment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {(users || []).map((user) => (
                    <tr key={user.id} className="hover:bg-muted/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-foreground flex items-center">
                            {user.userName}
                            {adminUsers.find(u => u.email === user.userEmail)?.isSuperAdmin && (
                              <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                Super Admin
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {user.userEmail}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.role === 'owner' 
                            ? 'bg-destructive/10 text-destructive'
                            : user.role === 'manager'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-secondary text-secondary-foreground'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.status === 'active' 
                            ? 'bg-secondary text-secondary-foreground'
                            : user.status === 'pending'
                            ? 'bg-accent text-accent-foreground'
                            : 'bg-destructive/10 text-destructive'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        <div className="flex items-center gap-1">
                          <Key className="h-4 w-4 text-muted-foreground" />
                          {user.apiKeysCount || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {(user as ExtendedClientUser).anthropicWorkspaceId ? (
                          <div className="flex items-center gap-1">
                            <span className="text-green-600 font-medium">Assigned</span>
                            <span className="text-xs text-muted-foreground">
                              ({(user as ExtendedClientUser).anthropicApiKeyPreview || '****'})
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Not assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openRoleAndKeyModal(user)}
                          className="text-primary hover:text-primary/80 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-destructive hover:text-destructive/80"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-border flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 text-sm font-medium text-foreground bg-background border border-input rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1 text-sm font-medium text-foreground bg-background border border-input rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <UserManagementModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        clientId={selectedClientId}
        user={null}
        onUserAdded={handleAddUser}
        onUserUpdated={() => {}}
      />

      {selectedUser && (
        <RoleAndKeyModal
          isOpen={showRoleAndKeyModal}
          onClose={() => setShowRoleAndKeyModal(false)}
          user={selectedUser}
          clientId={selectedClientId}
          onUserUpdated={refetch}
        />
      )}

      <PlatformSyncDialog
        isOpen={showPlatformSyncDialog}
        onClose={() => setShowPlatformSyncDialog(false)}
        clientId={selectedClientId}
        onSyncComplete={handleSyncComplete}
      />
    </div>
  );
};

export default UserManagement;